import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, Eye, Download, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface FileInfo {
  name: string;
  size: number;
  createdAt: string;
  type: string;
}

export const DataManagerView = () => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/list-files');
      const data = await response.json();
      if (data.success) {
        setFiles(data.files);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/upload-file', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('File uploaded successfully!');
        fetchFiles();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Network error during upload');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleReadFile = async (filename: string) => {
    setReading(true);
    setSelectedFile(filename);
    setError(null);
    try {
      const response = await fetch(`/api/read-file/${encodeURIComponent(filename)}`);
      const data = await response.json();
      if (data.success) {
        setFileContent(data.content);
      } else {
        setError(data.error || 'Failed to read file');
      }
    } catch (err) {
      console.error('Error reading file:', err);
      setError('Network error reading file');
    } finally {
      setReading(false);
    }
  };

  const handleDeleteFile = async (filename: string) => {
    if (!confirm(`Are you sure you want to delete ${filename}?`)) return;

    try {
      const response = await fetch(`/api/delete-file/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('File deleted');
        if (selectedFile === filename) {
          setSelectedFile(null);
          setFileContent(null);
        }
        fetchFiles();
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Manager</h1>
        <p className="text-gray-600">Upload and manage files on your hosting server.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upload & List Section */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold mb-4 flex items-center">
              <Upload className="mr-2 text-orange-500" size={20} />
              Upload New File
            </h2>
            <label className="relative flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {uploading ? (
                  <Loader2 className="animate-spin text-orange-500 mb-2" size={24} />
                ) : (
                  <Upload className="text-gray-400 mb-2" size={24} />
                )}
                <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-400 mt-1">JSON, CSV, TXT, etc.</p>
              </div>
              <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
            </label>

            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center">
                <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="mt-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm flex items-center">
                <CheckCircle2 size={16} className="mr-2 flex-shrink-0" />
                {success}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold mb-4 flex items-center">
              <FileText className="mr-2 text-orange-500" size={20} />
              Stored Files
            </h2>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-orange-500" size={24} />
              </div>
            ) : files.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm italic">No files uploaded yet.</p>
            ) : (
              <div className="space-y-3">
                {files.map((file) => (
                  <div 
                    key={file.name}
                    className={`p-3 rounded-xl border transition flex items-center justify-between group ${
                      selectedFile === file.name ? 'border-orange-500 bg-orange-50' : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className="flex-grow min-w-0 mr-3 cursor-pointer" onClick={() => handleReadFile(file.name)}>
                      <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                        {file.name.split('_').slice(1).join('_') || file.name}
                      </p>
                      <p className="text-xs text-gray-500">{formatSize(file.size)} • {new Date(file.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition">
                      <button 
                        onClick={() => handleReadFile(file.name)}
                        className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-white rounded-lg transition"
                        title="View Content"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteFile(file.name)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content Viewer Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 h-full flex flex-col overflow-hidden min-h-[500px]">
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <div className="flex items-center">
                <FileText className="text-orange-500 mr-2" size={20} />
                <span className="font-bold text-gray-900">
                  {selectedFile ? selectedFile.split('_').slice(1).join('_') : 'Select a file to view'}
                </span>
              </div>
              {fileContent && (
                <button 
                  onClick={() => {
                    const blob = new Blob([fileContent], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = selectedFile?.split('_').slice(1).join('_') || 'download.txt';
                    a.click();
                  }}
                  className="flex items-center text-xs font-bold text-orange-600 hover:text-orange-700 bg-white border border-orange-100 px-3 py-1.5 rounded-lg shadow-sm transition"
                >
                  <Download size={14} className="mr-1.5" /> Download
                </button>
              )}
            </div>

            <div className="flex-grow relative p-6 overflow-auto bg-gray-50/50">
              {reading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Loader2 className="animate-spin text-orange-500 mb-2" size={32} />
                  <p className="text-sm text-gray-500">Reading file content...</p>
                </div>
              ) : fileContent ? (
                <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap break-all bg-white p-4 rounded-xl border border-gray-100 shadow-inner">
                  {fileContent}
                </pre>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                  <Eye size={48} className="mb-4 opacity-20" />
                  <p className="text-sm italic">Select a file from the list to view its content here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
